import React, { memo, useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import { RootState } from 'redux/store';
import NotificationCard from 'components/NotificationCard/Index';
import Image from '../image/Image';
import Button from '../Button/Index';
import ProfileImage from 'components/ProfileImage';
import useLocales from 'hooks/useLocales';
import { headerDropdownItems } from 'core/constants/header';
import { CompanyService } from 'services/companes/companies';
import Popup from 'components/shared/Popups/Popup/Index';
import { headerDropdownItemsEnum } from 'core/enum/header-dropdown';
import { AuthService } from 'services/auth/auth';
import { NavbarActionEnum } from 'core/enum/navbar-action';
import { Role } from 'core/enum/role';
import { fetchNotifications } from '../../redux/slices/notification';
import DefaultImage from 'components/DefaultImage/Index';
import { IMe } from 'core/interface/me';
import UpgradePlanPopup from 'components/shared/Popups/UpgradePlan/Index';
import { clearAllState } from '../../redux/actions/clearState';
import NavbarNotificationButton from './NavbarNotificationButton';
import LanguagePopover from 'components/LanguagePopover';
import { ACCOUNT_ROUTES } from 'core/constants/accountRoutes';

import './index.scss';
import { useTenantInfo } from 'hooks/useTenantInfo';

const isCompanyRole = (role?: string): boolean =>
  role === 'company' || role === Role.RECRUITER || role === Role.ADMIN;

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLocales();
  const me = useSelector((state: IMe) => state.user).user;
  const isLoggedIn = !!me;
  const isCompanyUser = isLoggedIn && isCompanyRole(me?.role);
  const [showUpgrade, setShowUpgrade] = useState<boolean>(false);
  const [showDropdown, setShowDropDown] = useState<boolean>(false);
  const [showNotifications, setShowNotifications] = useState<boolean>(false);
  const { tenantLogo } = useTenantInfo();

  const [showAction, setShowAction] = useState<string>(
    NavbarActionEnum.SETTINGS
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const userActionsRef = useRef<HTMLDivElement>(null);
  const unreadCount = useSelector(
    (state: RootState) => state.notifications.unreadCount
  );

  useEffect(() => {
    if (isLoggedIn) {
      CompanyService.getCompanies(me?.company);
    }
  }, [isLoggedIn, me]);

  const toggleDropDown = (
    e: React.MouseEvent<HTMLSpanElement, MouseEvent>
  ): void => {
    e.stopPropagation();
    setShowNotifications(false);
    setShowAction(NavbarActionEnum.SETTINGS);
    setShowDropDown((prev) => !prev);
  };

  const handleLogOut = async (): Promise<void> => {
    clearAllState();
    try {
      await AuthService.logOut();
    } catch (error) {
      console.log(error);
    } finally {
      // Clear auth cookie regardless of API response; backend logout can fail intermittently.
      Cookies.remove('accesstoken', { path: '/' });
      Cookies.remove('accesstoken');
      // Force full app reset to avoid stale in-memory auth/router state.
      window.location.replace('/login');
    }
  };

  const handleMembership = () => {
    navigate('/membership');
  };

  const handleDropdownClick = (title: string): void => {
    switch (title.toLocaleLowerCase()) {
      case t(headerDropdownItemsEnum.SETTINGS).toLowerCase():
        setShowDropDown(false);
        navigate(ACCOUNT_ROUTES.SETTINGS);
        break;
      case t(headerDropdownItemsEnum.MEMBERSHIP).toLowerCase():
        handleMembership();
        break;
      case t(headerDropdownItemsEnum.SIGN_OUT).toLowerCase():
        handleLogOut();
        break;
      case t(headerDropdownItemsEnum.UPGRADE).toLowerCase():
        setShowUpgrade(true);
        break;
    }
  };

  const handleClosePopup = async (): Promise<void> => {
    setShowUpgrade(false);
    AuthService.getAccount();
  };

  const handleOpenNotifications = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): void => {
    e.stopPropagation();
    setShowDropDown(false);
    setShowAction(NavbarActionEnum.NOTIFICATIONS);
    setShowNotifications((prev) => !prev);
    fetchNotifications();
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        userActionsRef.current &&
        !userActionsRef.current.contains(event.target as Node)
      ) {
        setShowDropDown(false);
        setShowNotifications(false);
      }
    };

    document.addEventListener('click', handleOutsideClick);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, []);

  const dropdownItems = headerDropdownItems(t).filter((item) => {
    if (isCompanyUser && item.key === headerDropdownItemsEnum.UPGRADE) {
      return false;
    }
    if (me?.role !== 'admin' && item.key === headerDropdownItemsEnum.MEMBERSHIP) {
      return false;
    }
    return true;
  });

  const navigateToPublicJobs = () => {
    navigate('/jobs');
  };

  const navigateToPublicCompanies = () => {
    navigate('/companies');
  };

  const navigateToLogin = () => {
    navigate('/login');
  };

  const navigateToSignup = () => {
    navigate('/signup');
  };

  const showPublicNavLinks = !isCompanyUser;

  return (
    <nav>
      <div
        className="navbar"
        style={{
          marginBottom:
            useLocation().pathname.includes('/jobs') ||
            useLocation().pathname.includes('/companies')
              ? '0px'
              : '25px'
        }}
      >
        <a href="/" className="navbar-logo">
          {tenantLogo ? (
            <Image url={tenantLogo} width={210} />
          ) : (
            <Image name="new-rec-logos.svg" width={210} height={95} />
          )}
        </a>
        <div className="navbar__left">
          {showPublicNavLinks && (
            <>
              <div className="navbar__navigation">
                <div className="nav-link" onClick={navigateToPublicCompanies}>
                  {t('Companies')}
                </div>
              </div>

              <div
                className="navbar__navigation"
                style={{ marginRight: '20px' }}
              >
                <div className="nav-link" onClick={navigateToPublicJobs}>
                  {t('Jobs')}
                </div>
              </div>
            </>
          )}
          {isLoggedIn ? (
            <>
              <div
                className="navbar__left__user-actions"
                ref={userActionsRef}
              >
                <NavbarNotificationButton
                  unreadCount={unreadCount}
                  isActive={showNotifications}
                  onClick={handleOpenNotifications}
                />
                <LanguagePopover className="navbar__language-popover" />
                <div className="navbar__left__dropdown" ref={dropdownRef}>
                  <div
                    onClick={(e) => toggleDropDown(e)}
                    className="navbar__left__dropdown-btn flex items-center justify-between"
                  >
                    {me.avatar ? (
                      <ProfileImage
                        objectFit="cover"
                        height={44}
                        width={44}
                        name="profile.svg"
                        url={me.avatar}
                      />
                    ) : (
                      <DefaultImage
                        className="default-img"
                        height={44}
                        width={44}
                        letter={me?.first_name}
                      />
                    )}

                    <span className="navbar__left__dropdown-name">
                      {me.name ? me.name : ''}
                    </span>
                    <span>
                      <Image
                        className={`navbar__left__dropdown-arrow ${
                          showDropdown &&
                          showAction === NavbarActionEnum.SETTINGS
                            ? 'active'
                            : ''
                        }`}
                        name="arrow.svg"
                      />
                    </span>
                  </div>
                  {showDropdown &&
                    showAction === NavbarActionEnum.SETTINGS && (
                      <div className="navbar__left__dropdown__content">
                        <div className="navbar__left__dropdown__content__cards">
                          <ul className="navbar__left__dropdown__content__cards__ul">
                            {dropdownItems.map((item, index) => (
                              <li
                                onClick={() => handleDropdownClick(item.title)}
                                key={index}
                                className={`navbar__left__dropdown__content__cards__ul__li ${
                                  item.title === headerDropdownItemsEnum.UPGRADE
                                    ? 'upgrade-li'
                                    : ''
                                }`}
                              >
                                <Image
                                  name={item.icon}
                                  width={16}
                                  height={16}
                                />
                                <span className="navbar__left__dropdown__content__cards__ul__li-title">
                                  {item.title}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                </div>
                {showNotifications && (
                  <div className="navbar__notification-panel">
                    <NotificationCard />
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="navbar__auth">
              <LanguagePopover className="navbar__language-popover" />
              <div className="navbar__auth-buttons">
                <Button
                  onClick={navigateToLogin}
                  t={t}
                  className="navbar__auth-login"
                  title="Login"
                />
                <Button
                  onClick={navigateToSignup}
                  t={t}
                  className="navbar__auth-signup"
                  title="Sign Up"
                />
              </div>
            </div>
          )}
        </div>
      </div>
      {showUpgrade && (
        <Popup
          className="upgrade-plan-nav upgrade-popup-nav"
          title={t('Upgrade your plan')}
          description={t('Your subscription plan determines how many profiles you can unlock, how many new jobs you can post and how many AI credits you can use each month. Once you use up these credits, they won’t be replenished - even if you delete a job post. Any unused credits at the end of your billing cycle will roll over to the next month, so you don’t lose them.')}
          onClose={handleClosePopup}
        >
          <UpgradePlanPopup t={t} />
        </Popup>
      )}
    </nav>
  );
};

export default memo(Navbar);
